import {
  assertDatabaseImportPreflightAllowsInlineShardedMutation,
  assertDatabaseImportPreflightAllowsShardedMutation,
  DATABASE_IMPORT_STAGING_COLUMN_HARD_LIMIT,
  DATABASE_IMPORT_STAGING_ESTIMATED_BYTE_HARD_LIMIT,
  type DatabaseImportPreflightEstimate,
  estimateDatabaseImportPreflight,
} from '@streamlain/database-identity';
import Papa from 'papaparse';

import { excelWorkbookReader } from './excel-workbook-reader';
import type { BaseColumnDef } from './types';
import { columnName, detectColumnType } from './utils';

const PREVIEW_ROW_LIMIT = 10;
const TYPE_SAMPLE_ROW_LIMIT = 100;
const EXCEL_ROWS_TO_READ = TYPE_SAMPLE_ROW_LIMIT + 1;
const EXCEL_COLUMNS_TO_READ = DATABASE_IMPORT_STAGING_COLUMN_HARD_LIMIT;

export type ImportPreflightColumn = BaseColumnDef;

export type ImportSheetPreflight = {
  source: string;
  fileName: string;
  sheetName?: string;
  columns: ImportPreflightColumn[];
  previewRows: string[][];
  estimate: DatabaseImportPreflightEstimate;
};

export type ImportFilePreflight = {
  fileName: string;
  sheets: ImportSheetPreflight[];
};

export type ImportPreflightProgressPhase =
  | 'file-reading'
  | 'csv-reading'
  | 'workbook-reading'
  | 'workbook-loaded'
  | 'sheet-scanning'
  | 'preflight-complete';

export type ImportPreflightProgress = {
  phase: ImportPreflightProgressPhase;
  fileName: string;
  sheetName?: string;
  processedRows?: number;
  totalRows?: number;
};

export type ImportPreflightProgressHandler = (
  progress: ImportPreflightProgress
) => void;

export type CsvPreflightOptions = {
  file: File;
  hasHeader?: boolean;
  onProgress?: ImportPreflightProgressHandler;
};

export type ExcelPreflightOptions = {
  file: File;
  hasHeader?: boolean;
  selectedSheets?: readonly string[];
  onProgress?: ImportPreflightProgressHandler;
};

function createColumnsFromSamples(
  headerRow: readonly unknown[] | null,
  columnCount: number,
  sampleRows: readonly (readonly unknown[])[]
): ImportPreflightColumn[] {
  return Array.from({ length: columnCount }, (_, index) => {
    const headerValue = headerRow?.[index];
    const name =
      headerValue != null && String(headerValue).trim() !== ''
        ? String(headerValue).trim()
        : columnName(index);
    const values = sampleRows
      .map(row => row[index])
      .filter(value => value != null && String(value).trim() !== '');

    return {
      id: `preflight-column-${index}`,
      name,
      type: detectColumnType(values),
    };
  });
}

function stringifyRows(rows: readonly (readonly unknown[])[]): string[][] {
  return rows.map(row => row.map(cell => String(cell ?? '')));
}

export async function preflightCsvFile({
  file,
  hasHeader = true,
  onProgress,
}: CsvPreflightOptions): Promise<ImportSheetPreflight> {
  onProgress?.({ phase: 'file-reading', fileName: file.name });
  const text = await file.text();
  onProgress?.({ phase: 'csv-reading', fileName: file.name });

  return new Promise((resolve, reject) => {
    let rawRowIndex = 0;
    let dataRowCount = 0;
    let columnCount = 0;
    let headerRow: string[] | null = null;
    const previewRows: string[][] = [];
    const typeSampleRows: string[][] = [];

    Papa.parse<string[]>(text, {
      header: false,
      skipEmptyLines: true,
      dynamicTyping: false,
      step: result => {
        const row = Array.isArray(result.data) ? result.data : [];
        columnCount = Math.max(columnCount, row.length);

        if (hasHeader && rawRowIndex === 0) {
          headerRow = row;
          rawRowIndex += 1;
          return;
        }

        dataRowCount += 1;
        if (dataRowCount % 500 === 0) {
          onProgress?.({
            phase: 'csv-reading',
            fileName: file.name,
            processedRows: dataRowCount,
          });
        }
        if (previewRows.length < PREVIEW_ROW_LIMIT) {
          previewRows.push(row.map(cell => String(cell ?? '')));
        }
        if (typeSampleRows.length < TYPE_SAMPLE_ROW_LIMIT) {
          typeSampleRows.push(row);
        }
        rawRowIndex += 1;
      },
      complete: result => {
        if (result.errors.length > 0) {
          reject(
            new Error(
              `Failed to preflight CSV "${file.name}": ${result.errors[0]?.message}`
            )
          );
          return;
        }

        const columns = createColumnsFromSamples(
          headerRow,
          columnCount,
          typeSampleRows
        );
        const source = file.name;
        onProgress?.({
          phase: 'preflight-complete',
          fileName: file.name,
          processedRows: dataRowCount,
          totalRows: dataRowCount,
        });
        resolve({
          source,
          fileName: file.name,
          columns,
          previewRows,
          estimate: estimateDatabaseImportPreflight({
            source,
            rowCount: dataRowCount,
            columnCount,
            fileBytes: file.size,
            sampleRows: typeSampleRows,
          }),
        });
      },
      error: (error: Error) => {
        reject(error);
      },
    });
  });
}

export async function preflightExcelFile({
  file,
  hasHeader = true,
  selectedSheets,
  onProgress,
}: ExcelPreflightOptions): Promise<ImportFilePreflight> {
  if (file.size > DATABASE_IMPORT_STAGING_ESTIMATED_BYTE_HARD_LIMIT) {
    throw new Error(
      `Failed to preflight Excel "${file.name}": file has ${file.size} bytes; current import staging is limited to ${DATABASE_IMPORT_STAGING_ESTIMATED_BYTE_HARD_LIMIT} bytes.`
    );
  }

  onProgress?.({ phase: 'file-reading', fileName: file.name });
  const arrayBuffer = await file.arrayBuffer();
  onProgress?.({ phase: 'workbook-reading', fileName: file.name });
  const workbook = await excelWorkbookReader.read(arrayBuffer, {
    maxRowsToRead: EXCEL_ROWS_TO_READ,
    maxColumnsToRead: EXCEL_COLUMNS_TO_READ,
  });
  onProgress?.({ phase: 'workbook-loaded', fileName: file.name });
  const sheetNames = workbook.map(sheet => sheet.name);
  const sheetsToInspect = selectedSheets ?? sheetNames;
  const sheets: ImportSheetPreflight[] = [];

  for (const sheetName of sheetsToInspect) {
    if (!sheetNames.includes(sheetName)) {
      throw new Error(`Sheet not found in "${file.name}": ${sheetName}`);
    }

    const worksheet = workbook.find(sheet => sheet.name === sheetName);
    if (!worksheet) continue;

    onProgress?.({
      phase: 'sheet-scanning',
      fileName: file.name,
      sheetName,
    });
    const columnCount = worksheet.columnCount;
    const previewColumnCount = Math.min(
      columnCount,
      DATABASE_IMPORT_STAGING_COLUMN_HARD_LIMIT
    );
    const rowCount = Math.max(worksheet.rowCount - (hasHeader ? 1 : 0), 0);
    const rawSampleRows = worksheet.rows.slice(
      0,
      PREVIEW_ROW_LIMIT + (hasHeader ? 1 : 0)
    );
    const headerRow = hasHeader ? (rawSampleRows[0] ?? null) : null;
    const sampleDataRows = (
      hasHeader ? rawSampleRows.slice(1) : rawSampleRows
    ).slice(0, TYPE_SAMPLE_ROW_LIMIT);
    const source = `${file.name} / ${sheetName}`;

    sheets.push({
      source,
      fileName: file.name,
      sheetName,
      columns: createColumnsFromSamples(
        headerRow,
        previewColumnCount,
        sampleDataRows
      ),
      previewRows: stringifyRows(sampleDataRows.slice(0, PREVIEW_ROW_LIMIT)),
      estimate: estimateDatabaseImportPreflight({
        source,
        rowCount,
        columnCount,
        fileBytes: file.size,
        sampleRows: sampleDataRows,
      }),
    });
  }

  onProgress?.({
    phase: 'preflight-complete',
    fileName: file.name,
    totalRows: sheets.reduce((sum, sheet) => sum + sheet.estimate.rowCount, 0),
  });

  return {
    fileName: file.name,
    sheets,
  };
}

export function assertImportPreflightAllowsInlineShardedMutation(
  preflights: readonly ImportSheetPreflight[]
): void {
  for (const preflight of preflights) {
    assertDatabaseImportPreflightAllowsInlineShardedMutation(
      preflight.estimate
    );
  }
}

export function assertImportPreflightAllowsShardedMutation(
  preflights: readonly ImportSheetPreflight[]
): void {
  for (const preflight of preflights) {
    assertDatabaseImportPreflightAllowsShardedMutation(preflight.estimate);
  }
}
